import { useState } from 'react'
import { Check } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useCreateCompany } from '../../hooks/api/useCompanies'
import { useUpdateUser } from '../../hooks/api/useUsers'
import { useUserCheck } from '../../hooks/api/useUserCheck'
import { supabase } from '../../lib/supabase'
import { generateCompanySlug } from '../../utils/slug'

interface SubscriptionPlan {
  id: string
  name: string
  price: string
  description: string
  features: string[]
  isPopular?: boolean
  isTrial?: boolean
}

const subscriptionPlans: SubscriptionPlan[] = [
  {
    id: 'essentials',
    name: 'Essentials',
    price: '$50/Computer',
    description: 'Handle the basics before they become problems',
    features: [
      'Remote help desk for end-user support',
      'Real-time hardware and software asset tracking',
      'System health checks and scheduled monitoring',
      'Patch management for software and operating systems',
      'Email spam filtering and malware protection',
      'Antivirus and ransomware protection',
      'SaaS backups (e.g., Microsoft 365)',
      'Basic cybersecurity training',
      'Dark web exposure monitoring',
      'Local and cloud backup with recovery support'
    ]
  },
  {
    id: 'enhanced',
    name: 'Enhanced',
    price: '$75/Computer',
    description: 'For growing businesses that can\'t afford IT blind spots',
    features: [
      'Full network audit and optimization',
      'Backup and disaster recovery strategy',
      'Advanced endpoint threat protection',
      'SaaS integration (Microsoft 365, Google Workspace)',
      'Vendor management and third-party coordination',
      'Quarterly business reviews',
      'Advanced email threat protection',
      'Monthly reporting with actionable insights'
    ],
    isPopular: true
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '$100/Computer',
    description: 'Enterprise-grade protection, built for modern teams',
    features: [
      '24/7 remote monitoring and support',
      'Infrastructure management across multiple sites',
      'Custom reporting tailored to your business',
      'Cybersecurity awareness training ($4 per user)',
      'Multi-factor authentication setup ($2.80 per user)',
      'Server-level alerting and suspicious activity monitoring ($17 per node)',
      'Full compliance preparation for SOC2, HIPAA, PCI, and CMMC',
      'Policy creation and audit-ready documentation'
    ]
  }
]

export const SubscriptionPage = () => {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()
  const planFromUrl = searchParams.get('plan')
  const createCompany = useCreateCompany()
  const updateUser = useUpdateUser()
  const { data: userRecord, isLoading: userLoading } = useUserCheck(user?.id || '')

  // Find matching plan or default to Enhanced
  const initialPlan = subscriptionPlans.find(p => p.id === planFromUrl) || subscriptionPlans[1]
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>(initialPlan)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Show loading while checking user data
  if (userLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }


  const handleContinue = async (plan: SubscriptionPlan) => {
    if (!user) return

    setIsLoading(true)
    setErrorMessage(null)
    try {
      let companyId = userRecord?.company_id

      // If user doesn't have a company yet, create one
      if (!companyId) {
        const organizationName =
          (user.user_metadata?.organization_name as string | undefined)?.trim() ||
          'Your Company'

        const companyName = organizationName || 'Your Company'
        const companySlug = generateCompanySlug(companyName)

        // Create company with selected plan
        const company = await createCompany.mutateAsync({
          name: companyName,
          slug: companySlug,
          subscription_tier: plan.id as 'essentials' | 'enhanced' | 'premium'
        })

        companyId = company.id

        // Link user to the company
        await updateUser.mutateAsync({
          id: user.id,
          company_id: company.id,
          full_name: (user.user_metadata?.full_name as string | undefined) ?? user.email ?? undefined
        })

        await supabase.auth.updateUser({
          data: {
            ...user.user_metadata,
            company_id: company.id,
            company_name: companyName
          }
        })
      }

      // Create Stripe checkout session for the company
      const { data, error } = await supabase.functions.invoke('stripe-create-checkout-session', {
        body: {
          tier: plan.id,
          company_id: companyId,
          success_url: `${window.location.origin}/subscription/success`,
          cancel_url: `${window.location.origin}/subscription`
        }
      })

      if (error) {
        console.error('Stripe checkout error:', error)
        setErrorMessage('Unable to start checkout. Please try again.')
        return
      }

      // Redirect to Stripe checkout
      if (data?.url) {
        window.location.href = data.url
      } else {
        setErrorMessage('Unable to start checkout. Please try again.')
      }
    } catch (error) {
      console.error('Error in subscription flow:', error)
      setErrorMessage('Something went wrong. Please try again or contact support.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-4 sm:p-6 md:p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        {/* Logo */}
        <div className="mb-6 sm:mb-8">
          <img src="/black_logo.svg" alt="Helpdesk 411" className="h-8 sm:h-10 md:h-12 w-auto" />
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="flex space-x-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`h-2.5 sm:h-3 rounded-full ${
                  step === 1 ? 'bg-red-500 w-[50px]' : 'bg-gray-300 w-5'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <h1 className="text-lg md:text-2xl font-medium text-gray-900 mb-3 sm:mb-2 px-4">
            Choose Subscription
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base px-4">
            Choose the right level of protection for your business. Every plan is designed to scale with your growth.
          </p>
        </div>

        {errorMessage && (
          <div className="mx-auto mb-6 max-w-2xl rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {errorMessage}
          </div>
        )}

        {/* Main Content - Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {subscriptionPlans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan)}
              className={`bg-white rounded-2xl p-6 lg:p-8 cursor-pointer transition-all duration-200 flex flex-col border-2 ${
                selectedPlan.id === plan.id
                  ? 'border-red-500 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}
            >
              {/* Plan Header */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="text-xl font-medium text-gray-900 mb-2">{plan.price}</div>
                <p className="text-gray-400 text-sm leading-relaxed">{plan.description}</p>
              </div>

              {/* Features List */}
              <div className="flex-1 mb-8">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0 mr-3" />
                      <span className="text-gray-400 text-xs">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Subscribe Button */}
              <button
                onClick={() => handleContinue(plan)}
                disabled={isLoading}
                className={`w-full py-3 px-6 rounded-full font-semibold transition-colors duration-200 text-sm ${
                  plan.isPopular
                    ? 'bg-gray-900 hover:bg-gray-800 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isLoading ? 'Setting up...' : 'Subscribe'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
